// src/lib/jewishHolidays.ts
//
// Maps the major demand-driving Jewish holidays to their ISO week-of-year for a
// given Gregorian year, so the inventory seasonal-window panel can label which
// holiday drives a recurring sales spike (e.g. "weeks 13–17 · Passover").
//
// Computed CLIENT-SIDE from the same tested Hebrew↔Gregorian conversion the
// HebrewDatePicker uses (hebrewToGregorian) — no per-date API calls. Holidays
// drift ±1–2 ISO weeks year to year on the lunar calendar, so callers match
// against a window with a small tolerance.

import { hebrewToGregorian } from '../components/HebrewDatePicker';

// Hebrew month numbers match HebrewDatePicker: Tishrei=7 … Elul=6; Adar=12,
// Adar II=13, Nissan=1, Sivan=3, Kislev=9.
const HOLIDAYS: Array<{ name: string; hm: number; hd: number }> = [
  { name: 'Rosh Hashanah', hm: 7,  hd: 1  },
  { name: 'Yom Kippur',    hm: 7,  hd: 10 },
  { name: 'Sukkot',        hm: 7,  hd: 15 },
  { name: 'Chanukah',      hm: 9,  hd: 25 },
  { name: 'Purim',         hm: 12, hd: 14 }, // Adar (Adar II in a leap year — handled below)
  { name: 'Passover',      hm: 1,  hd: 15 }, // Nissan
  { name: 'Shavuot',       hm: 3,  hd: 6  }, // Sivan
];

// Hebrew leap year (same rule as HebrewDatePicker.isHebrewLeapYear).
function isHebrewLeapYear(hy: number): boolean {
  return ((7 * hy) + 1) % 19 < 7;
}

// ISO 8601 week-of-year (weeks start Monday; week 1 contains the first Thursday).
function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (t.getUTCDay() + 6) % 7;          // Mon=0 … Sun=6
  t.setUTCDate(t.getUTCDate() - day + 3);        // Thursday of this week
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const firstThuDay = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstThuDay + 3);
  return 1 + Math.round((t.getTime() - firstThu.getTime()) / (7 * 24 * 3600 * 1000));
}

export interface HolidayWeek { name: string; week: number; }

/** Major Jewish holidays that fall within the given Gregorian year, with the
 *  ISO week each lands in. Spring holidays (Purim/Passover/Shavuot) sit in
 *  Hebrew year greg+3760; autumn (Rosh Hashanah/Yom Kippur/Sukkot/Chanukah) in
 *  greg+3761 — so we try both and keep the occurrence that lands in `gregYear`. */
export function holidaysForGregorianYear(gregYear: number): HolidayWeek[] {
  const out: HolidayWeek[] = [];
  for (const h of HOLIDAYS) {
    for (const hy of [gregYear + 3760, gregYear + 3761]) {
      const hm = (h.name === 'Purim' && isHebrewLeapYear(hy)) ? 13 : h.hm;
      let g: Date;
      try { g = hebrewToGregorian(hy, hm, h.hd); } catch { continue; }
      if (g.getFullYear() === gregYear) out.push({ name: h.name, week: isoWeek(g) });
    }
  }
  return out;
}

/** Holiday names whose ISO week falls within [startWoy, endWoy] (±`tolerance`
 *  weeks for lunar drift). De-duplicated, in calendar order. */
export function holidaysInWeekRange(
  startWoy: number,
  endWoy: number,
  gregYear: number,
  tolerance = 1,
): string[] {
  const seen = new Set<string>();
  return holidaysForGregorianYear(gregYear)
    .filter((h) => h.week >= startWoy - tolerance && h.week <= endWoy + tolerance)
    .filter((h) => (seen.has(h.name) ? false : (seen.add(h.name), true)))
    .map((h) => h.name);
}

// Named NON-lunar seasons — fixed Gregorian ISO-week ranges that drive recurring
// demand independent of the Hebrew calendar. Add more here as patterns are
// confirmed (the seasonal detector already proves recurrence: ≥3 of 5 years).
const NAMED_SEASONS: Array<{ name: string; startWeek: number; endWeek: number }> = [
  // Catskills summer relocation — city families move up for the summer, so local
  // stores stock up. Late June (~ISO weeks 25-26). Joel 2026-06-30.
  { name: 'Summer season', startWeek: 24, endWeek: 27 },
];

/** Seasonal labels (named seasons + Jewish holidays) overlapping the window
 *  [startWoy, endWoy] (±tolerance). Named seasons first, then holidays. */
export function seasonalLabelsInWeekRange(
  startWoy: number,
  endWoy: number,
  gregYear: number,
  tolerance = 1,
): string[] {
  const seasons = NAMED_SEASONS
    .filter((s) => s.startWeek <= endWoy + tolerance && s.endWeek >= startWoy - tolerance)
    .map((s) => s.name);
  return [...seasons, ...holidaysInWeekRange(startWoy, endWoy, gregYear, tolerance)];
}
